import Add10pxIcon from '@jetbrains/icons/add-10px.svg'
import Remove10pxIcon from '@jetbrains/icons/remove-10px.svg'
import Button from '@jetbrains/ring-ui/components/button/button'
import Checkbox from '@jetbrains/ring-ui/components/checkbox/checkbox'
import {H3} from '@jetbrains/ring-ui/components/heading/heading'
import classNames from 'classnames'
import * as React from 'react'

import useEventCallback from '../../../../../../../hooks/useEventCallback'
import {getBuildType} from '../../../../../../../selectors'
import {emptyObject} from '../../../../../../../utils/empty'
import {objectKeys} from '../../../../../../../utils/object'
import type {CleanupHolderNode, KeepBuildParts} from '../../Rules.types'
import {LoadingContext, DispatchContext} from '../CleanupForm.contexts'
import styles from '../CleanupForm.css'
import type {CleanupFormValidationErrors} from '../CleanupForm.types'

import ArtifactsSection from './ArtifactsSection'

import {useAppSelector} from 'src/hooks/react-redux'

type OwnProps = {
  setError: (arg0: CleanupFormValidationErrors) => unknown
  value: KeepBuildParts
  holderNode: CleanupHolderNode
}
const DEFAULT_ARTIFACT_PATTERN = '+:**/*'
const defaultPreserveParams: KeepBuildParts = {
  everything: emptyObject,
  history: emptyObject,
  artifacts: {
    artifactPatterns: DEFAULT_ARTIFACT_PATTERN,
  },
  logs: emptyObject,
  statistics: emptyObject,
}
const captions = {
  everything: 'Everything',
  history: 'History',
  artifacts: 'Artifacts',
  logs: 'Logs',
  statistics: 'Statistics',
}
export const preserveConverter = {
  toForm(value: KeepBuildParts | null | undefined): KeepBuildParts {
    const formValue = value || defaultPreserveParams
    return {
      ...formValue,
      artifacts:
        formValue.artifacts != null
          ? {
              ...formValue.artifacts,
              artifactPatterns:
                (formValue.artifacts.artifactPatterns ?? '') || DEFAULT_ARTIFACT_PATTERN,
            }
          : null,
    }
  },

  toRule: (value: KeepBuildParts): KeepBuildParts => value,
}
const ERROR_DESCRIPTION = {
  notEmpty: 'You must select at least one option',
}

const checkIfNested = (name: keyof KeepBuildParts) => ['artifacts', 'logs'].includes(name)

const getCaption = (name: keyof KeepBuildParts) => captions[name]

const isEverythingChecked = (value: KeepBuildParts): boolean =>
  !Object.entries(value).some(
    ([key, currentValue]) => key !== 'everything' && currentValue == null,
  ) && value.artifacts?.artifactPatterns === ''

function PreserveSection({setError, value, holderNode}: OwnProps) {
  const isComposite = useAppSelector(
    state =>
      holderNode.nodeType === 'bt' && getBuildType(state, holderNode.id)?.type === 'composite',
  )
  const disabled = React.useContext(LoadingContext)
  const artifactsPattern = React.useRef<string>(value.artifacts?.artifactPatterns ?? '')
  const setValue = React.useContext(DispatchContext)
  const onChange = React.useCallback(
    (values: KeepBuildParts) =>
      setValue?.({
        name: 'preserve',
        value: values,
      }),
    [setValue],
  )
  const [artifactsInputVisible, setArtifactsInputVisible] = React.useState<boolean>(
    value.artifacts?.artifactPatterns != null &&
      value.artifacts.artifactPatterns !== DEFAULT_ARTIFACT_PATTERN,
  )
  const toggleArtifactsPatternVisibility = React.useCallback(() => {
    onChange({
      ...value,
      everything: isEverythingChecked({
        ...value,
        artifacts: {
          artifactPatterns: !artifactsInputVisible ? artifactsPattern.current : '',
        },
      })
        ? emptyObject
        : null,
      artifacts: {
        artifactPatterns: artifactsInputVisible ? artifactsPattern.current : '',
      },
    })
    setArtifactsInputVisible(state => !state)
  }, [artifactsInputVisible, onChange, value])
  const handleCheckboxChange = useEventCallback((event: React.SyntheticEvent<HTMLInputElement>) => {
    const {checked, name} = event.currentTarget
    const data = {...value, [name]: checked ? emptyObject : null}

    if (name === 'history' && !checked) {
      data.artifacts = null
      data.logs = null
    }

    if (name === 'logs') {
      data.logs = checked ? emptyObject : null

      if (checked) {
        data.history = emptyObject
      }
    }

    if (name === 'everything') {
      const nextArtifactsVisibility = checked ? false : artifactsPattern.current !== ''
      data.logs = checked ? emptyObject : null
      data.history = checked ? emptyObject : null
      data.statistics = emptyObject
      data.artifacts = checked
        ? {
            artifactPatterns: nextArtifactsVisibility ? artifactsPattern.current : '',
          }
        : null
      setArtifactsInputVisible(nextArtifactsVisibility)
    }

    if (name === 'artifacts') {
      data.artifacts = checked
        ? {
            artifactPatterns: artifactsPattern.current,
          }
        : null
      setArtifactsInputVisible(checked && artifactsPattern.current !== '')

      if (checked) {
        data.history = emptyObject
      }
    }

    data.everything = isEverythingChecked(data) ? emptyObject : null
    onChange(data)
  })
  const onArtifactsPatternChange = React.useCallback(
    (data: string) => {
      artifactsPattern.current = data
      const valueToSave = {
        ...value,
        artifacts: {
          artifactPatterns: artifactsPattern.current,
        },
      }
      onChange({...valueToSave, everything: isEverythingChecked(valueToSave) ? emptyObject : null})
    },
    [onChange, value],
  )
  const itemsToRender = React.useMemo(
    () =>
      objectKeys(defaultPreserveParams).map(name =>
        name === 'artifacts' && isComposite ? null : (
          <div
            key={name}
            className={classNames(styles.checkbox, checkIfNested(name) && styles.nested)}
          >
            <Checkbox
              disabled={disabled || name === 'statistics'}
              className={styles.checkbox}
              name={name}
              checked={!!value[name]}
              onChange={handleCheckboxChange}
            >
              {getCaption(name)}
            </Checkbox>
            {name === 'artifacts' && !!value[name] && (
              <Button
                onClick={toggleArtifactsPatternVisibility}
                className={styles.preserveArtifactsToggle}
                icon={artifactsInputVisible ? Remove10pxIcon : Add10pxIcon}
                primary
                disabled={disabled}
              >
                {'Certain pattern'}
              </Button>
            )}
            {name === 'artifacts' && !!value[name] && artifactsInputVisible && (
              <ArtifactsSection
                disabled={disabled}
                className={styles.preserveArtifactsInput}
                value={artifactsPattern.current ?? ''}
                onChange={onArtifactsPatternChange}
              />
            )}
          </div>
        ),
      ),
    [
      isComposite,
      disabled,
      value,
      handleCheckboxChange,
      toggleArtifactsPatternVisibility,
      artifactsInputVisible,
      onArtifactsPatternChange,
    ],
  )
  const currentError = React.useMemo(() => {
    let error = null

    if (!Object.values(value).some(item => item != null)) {
      error = ERROR_DESCRIPTION.notEmpty
    }

    return error
  }, [value])
  React.useEffect(() => {
    setError({
      preserve: currentError,
    })
  }, [setError, currentError])
  return (
    <div>
      <H3 className={styles.title}>{'Build Data'}</H3>
      <span className={styles.hint}>{'Defines what build data to preserve'}</span>
      <div className={classNames(styles.inputs, styles.checkboxContainer, styles.rowGroup)}>
        {itemsToRender}
      </div>
      {currentError && <div className={styles.error}>{currentError}</div>}
    </div>
  )
}

export default React.memo<OwnProps>(PreserveSection)
